import {
  WSTable,
  WSTableCell,
  WSTableColumn,
  WSTableItem,
  WSTableRowMenuAction
} from "@wingspanhq/fe-component-library";
import { PayeeRequirement } from "@wingspanhq/wingspan-api-sdk";
import React from "react";

import {
  RequirementsListView,
  selectorRequirementsListView
} from "../selectors/selectorRequirementsListView";
import {
  ExtendedRequirementTableItemData,
  RequirementAdditionalData,
  RequirementContextType,
  RequirementTableItemData
} from "../types";
import { useRequirementsConfig } from "../useRequirementsConfig";
import {
  getIsCustomRequirement,
  getIsGeneralRequirement,
  getRequirementListKey
} from "../utils";

import { RequirementsEmptyState } from "./RequirementsEmptyState";

export type RequirementsTableProps = {
  requirements: Array<ExtendedRequirementTableItemData>;
  contextType: RequirementContextType;
  hideActions?: boolean;
  compact?: boolean;
  hideTaxVerificationConsent?: boolean;
  requiredAction?: string;
  showEngagementsView?: boolean;
  /**
   * The *list* read is still in flight. Distinct from the config's own
   * `isLoading`, which covers the per-row data-source documents: without this an
   * unresolved list is indistinguishable from an empty one, and the table
   * renders "No requirements active" over a request that has not returned.
   */
  isLoading?: boolean;
  getOnViewDetails: (
    item: PayeeRequirement,
    additionalData?: RequirementAdditionalData
  ) => ((item: PayeeRequirement) => void) | undefined;
};

export const ExtendedRequirementsTable: React.FC<RequirementsTableProps> = ({
  requirements,
  contextType,
  hideActions,
  compact,
  hideTaxVerificationConsent,
  getOnViewDetails,
  showEngagementsView,
  requiredAction,
  isLoading
}) => {
  const requirementsConfig = useRequirementsConfig({
    requirements: requirements.filter(
      requirement => !getIsCustomRequirement(requirement)
    ) as RequirementTableItemData[],
    contextType,
    context: {
      hideTaxVerificationConsent
    }
  });

  const tableData: WSTableItem<ExtendedRequirementTableItemData>[] =
    requirements.map((requirement, index) => {
      return {
        id: getRequirementListKey(requirement, index),
        data: requirement
      };
    });

  const columns: Array<WSTableColumn<ExtendedRequirementTableItemData>> = [
    {
      config: {
        header: {
          text: "Requirement"
        },
        gridTemplateSizeMax: "0.5fr"
      },
      renderFunction: rowItem => {
        if (getIsCustomRequirement(rowItem.data)) {
          return <WSTableCell {...rowItem.data.nameCell} />;
        }

        return (
          <WSTableCell {...requirementsConfig.getNameCellProps(rowItem.data)} />
        );
      }
    },
    {
      config: {
        header: {
          text: "Type"
        },
        hideOnScreens: compact ? ["XS", "S", "M", "L", "XL"] : ["XS"],
        gridTemplateSizeMax: "0.5fr"
      },
      renderFunction: rowItem => {
        if (getIsCustomRequirement(rowItem.data)) {
          return <WSTableCell {...rowItem.data.typeCell} />;
        }

        return (
          <WSTableCell {...requirementsConfig.getTypeCellProps(rowItem.data)} />
        );
      }
    },
    {
      config: {
        hideOnScreens:
          showEngagementsView || contextType === RequirementContextType.Payee
            ? ["XS", "S", "M", "L", "XL"]
            : [],
        header: {
          text: "Clients"
        },
        gridTemplateSizeMax: "0.4fr"
      },
      renderFunction: rowItem => {
        if (getIsCustomRequirement(rowItem.data)) {
          return <WSTableCell {...rowItem.data.clientsCell} />;
        }

        return (
          <WSTableCell
            {...requirementsConfig.getClientsCellProps(rowItem.data)}
          />
        );
      }
    },
    {
      config: {
        hideOnScreens:
          !showEngagementsView && contextType === RequirementContextType.Payer
            ? ["XS", "S", "M", "L", "XL"]
            : [],
        header: {
          text: "Engagements"
        },
        gridTemplateSizeMax: "0.4fr"
      },
      renderFunction: rowItem => {
        if (getIsCustomRequirement(rowItem.data)) {
          return <WSTableCell {...rowItem.data.engagementsCell} />;
        }

        return (
          <WSTableCell
            {...requirementsConfig.getEngagementsCellProps(rowItem.data)}
          />
        );
      }
    },
    {
      config: {
        header: {
          text: "Status"
        },
        gridTemplateSizeMax: "0.5fr"
      },
      renderFunction: rowItem => {
        if (getIsCustomRequirement(rowItem.data)) {
          return <WSTableCell {...rowItem.data.statusCell} />;
        }

        return (
          <WSTableCell
            {...requirementsConfig.getStatusCellProps(rowItem.data)}
          />
        );
      }
    }
  ];

  const listView = selectorRequirementsListView({
    count: requirements.length,
    isLoading: !!isLoading
  });

  // Only "no requirements" once the list has actually resolved; while it is in
  // flight the table below renders its own shimmer rows instead.
  if (listView === RequirementsListView.Empty) {
    return <RequirementsEmptyState />;
  }

  return (
    <WSTable<ExtendedRequirementTableItemData>
      key={requirementsConfig.dataUpdatedAt}
      columns={columns}
      data-testid="requirementsTable"
      getOnRowClick={({ data }) => {
        if (getIsCustomRequirement(data) || getIsGeneralRequirement(data)) {
          return undefined;
        }

        const additionalData = requirementsConfig.getAdditionalData(data);
        const callback = getOnViewDetails(data, additionalData);

        return callback
          ? ({ data }) => callback(data as PayeeRequirement)
          : undefined;
      }}
      loading={
        listView === RequirementsListView.Loading ||
        requirementsConfig.isLoading
      }
      rowActions={({ data }) => {
        if (getIsCustomRequirement(data) || hideActions || !requiredAction) {
          return [];
        }

        const isActionRequired = requirementsConfig.isActionRequired(data);

        if (isActionRequired) {
          return [
            {
              text: requiredAction,
              onClick: () => {
                const additionalData =
                  requirementsConfig.getAdditionalData(data);
                const callback = getOnViewDetails(data, additionalData);

                return callback && callback(data);
              }
            }
          ];
        }

        return [];
      }}
      rowMenuActions={({ data }) => {
        if (getIsCustomRequirement(data)) {
          return data.menuActions || [];
        }
        if (hideActions) {
          return [];
        }

        const viewDetails: WSTableRowMenuAction<RequirementTableItemData> = {
          label: "View requirement",
          icon: "view",
          onClick: () => {
            const additionalData = requirementsConfig.getAdditionalData(data);

            const callback = getOnViewDetails(data, additionalData);

            return callback && callback(data);
          }
        };

        const hasViewDetails = !!getOnViewDetails(data);

        if (hasViewDetails && !getIsGeneralRequirement(data)) {
          return [
            viewDetails,
            ...requirementsConfig.renderRowMenuActions(data)
          ];
        }

        return requirementsConfig.renderRowMenuActions(data);
      }}
      tableData={tableData}
    />
  );
};
